/**
 * Fixtures do MOCK MODE — dados realistas para TODOS os /admin/*.
 *
 * São deterministas (gerados a partir de um PRNG com seed fixa) para que a
 * lista, o getOne e o update sejam consistentes entre reloads. Cada array aqui
 * corresponde 1:1 a um resource do Refine (o `name` em resources/definitions.tsx):
 *
 *   users          → User[]          (~30 contas: plano/role/banned/quota)
 *   plans          → PlanRecord[]    (billing/MRR/status derivado dos users premium)
 *   ai-metrics     → AiMetric[]      (por dia × provider × kind, com cost_usd)
 *   entries        → Entry[]         (~40 entradas p/ moderação, modos e flagged)
 *   brains         → Brain[]         (contextos compartilhados p/ moderação)
 *   corrections    → Correction[]    (matriz de→para de categoria)
 *   feature-flags  → FeatureFlag[]   (8 flags)
 *   health         → HealthCheck[]   (componentes do sistema)
 *   audit-log      → AdminAuditLog[] (~40 eventos: ban/unban/impersonate/flag)
 *
 * Tipos vêm de src/lib/types.ts. Campos extras (ex.: User.banned, User.nome)
 * são adicionados aqui via interseção para alimentar telas futuras sem quebrar
 * o contrato base.
 */
import type {
  User,
  PlanRecord,
  AiMetric,
  Entry,
  Brain,
  Correction,
  FeatureFlag,
  HealthCheck,
  AdminAuditLog,
  AuditAction,
  Category,
  Plan,
  Role,
  Modo,
  AuthProvider,
} from "@/lib/types";

/** User do mock acrescido de `banned` e `nome` (consumidos por telas futuras). */
export type MockUser = User & { banned: boolean; nome: string | null };

// ── PRNG determinista (mulberry32) ────────────────────────────────────────────
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rnd = mulberry32(20260622);

const pick = <T>(arr: readonly T[]): T => arr[Math.floor(rnd() * arr.length)];
const between = (min: number, max: number): number =>
  Math.floor(rnd() * (max - min + 1)) + min;
const chance = (p: number): boolean => rnd() < p;
const round = (n: number, d = 2): number => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

/** ISO a partir de "hoje" (2026-06-22) menos N dias, com hora aleatória. */
const NOW = new Date("2026-06-22T12:00:00.000Z").getTime();
const DAY = 86_400_000;
const daysAgo = (d: number, jitterH = 23): string =>
  new Date(NOW - d * DAY - between(0, jitterH) * 3_600_000).toISOString();
const ymd = (d: number): string =>
  new Date(NOW - d * DAY).toISOString().slice(0, 10);

// ── Vocabulário pt-BR realista ─────────────────────────────────────────────────
const FIRST = [
  "Ana", "Bruno", "Carla", "Diego", "Eduarda", "Felipe", "Gabriela", "Henrique",
  "Isabela", "João", "Larissa", "Marcos", "Natália", "Otávio", "Paula", "Rafael",
  "Sofia", "Thiago", "Vanessa", "William", "Yara", "Zé", "Camila", "Lucas",
  "Mariana", "Pedro", "Renata", "Tatiane", "Vitor", "Beatriz",
];
const LAST = [
  "Silva", "Souza", "Oliveira", "Santos", "Pereira", "Costa", "Almeida",
  "Lima", "Gomes", "Ribeiro", "Carvalho", "Araújo", "Melo", "Barbosa",
];
const CATEGORIES: Category[] = ["lembrete", "evento", "saude", "tarefa", "nota"];
const MODOS: Modo[] = ["normal", "reuniao", "desabafo"];
const PROVIDERS_AUTH: AuthProvider[] = ["password", "google", "apple"];

// Texto de entrada por categoria (sem dados sensíveis reais).
const ENTRY_TEXT: Record<Category, string[]> = {
  lembrete: [
    "Lembrar de pagar o boleto da internet dia 10",
    "Buscar as crianças na escola às 17h",
    "Renovar a assinatura do streaming amanhã",
    "Ligar para o dentista e remarcar",
  ],
  evento: [
    "Reunião de alinhamento com o time de produto na quinta 14h",
    "Aniversário da Mariana no sábado",
    "Show no Allianz dia 30, comprar ingresso",
    "Viagem para o interior no feriado",
  ],
  saude: [
    "Tomar o remédio da pressão todo dia às 8h",
    "Consulta com a cardiologista terça que vem",
    "Beber 2L de água por dia",
    "Exame de sangue em jejum na sexta",
  ],
  tarefa: [
    "Terminar o relatório trimestral e enviar pro gestor",
    "Comprar presente de aniversário",
    "Organizar a planilha de gastos do mês",
    "Responder os e-mails pendentes do cliente",
  ],
  nota: [
    "Ideia: app de receitas com lista de compras integrada",
    "Resumo do livro que estou lendo sobre hábitos",
    "Senha do wifi do escritório fica no quadro",
    "Anotação rápida da call: priorizar onboarding",
  ],
};

const BRAIN_NAMES = [
  ["Família", "👨‍👩‍👧‍👦"], ["Trabalho", "💼"], ["Saúde", "❤️"], ["Estudos", "📚"],
  ["Projeto MEL", "🧠"], ["Casa", "🏠"], ["Financeiro", "💰"], ["Viagens", "✈️"],
  ["Academia", "🏋️"], ["Pessoal", "✨"], ["Time Produto", "🚀"], ["Receitas", "🍳"],
];

// ── USERS (~30) ────────────────────────────────────────────────────────────────
function buildUsers(): MockUser[] {
  const users: MockUser[] = [];
  // admin fixo (espelha o admin fake logado do authProvider)
  users.push({
    id: "usr_admin_0001",
    email: "admin@melapp.com.br",
    provider: "password",
    email_verified: true,
    role: "admin",
    plano: "premium",
    tipo_cobranca: "none",
    registros_mes: 12,
    registros_extras: 0,
    weekly_email: true,
    onboarding_done: true,
    contexto_pessoal: "Conta administrativa interna do MEL.",
    stripe_customer_id: null,
    data_renovacao: null,
    created_at: daysAgo(420),
    banned: false,
    nome: "Equipe MEL",
  });

  for (let i = 0; i < 29; i++) {
    const first = pick(FIRST);
    const last = pick(LAST);
    const nome = `${first} ${last}`;
    const slug = `${first}.${last}`.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    const plano: Plan = chance(0.42) ? "premium" : "free";
    const tipo_cobranca: User["tipo_cobranca"] =
      plano === "premium" ? (chance(0.7) ? "monthly" : "annual") : "none";
    const registros_mes =
      plano === "premium" ? between(5, 480) : between(0, 30);
    // garante alguns banidos (índices 6, 14, 22) + chance extra, para as telas
    // de moderação/usuários sempre terem dados de "banido".
    const banned = i === 6 || i === 14 || i === 22 || chance(0.06);
    const provider = pick(PROVIDERS_AUTH);
    const createdDays = between(2, 400);
    users.push({
      id: `usr_${String(i + 2).padStart(4, "0")}`,
      email: `${slug}${between(1, 99)}@${pick(["gmail.com", "outlook.com", "hotmail.com", "icloud.com"])}`,
      provider,
      email_verified: provider !== "password" ? true : chance(0.85),
      role: "user" as Role,
      plano,
      tipo_cobranca,
      registros_mes,
      registros_extras: plano === "free" && chance(0.3) ? between(1, 20) : 0,
      weekly_email: chance(0.6),
      onboarding_done: chance(0.8),
      contexto_pessoal: chance(0.4)
        ? `Usa o MEL principalmente para ${pick(["organização pessoal", "trabalho", "saúde da família", "estudos"])}.`
        : null,
      stripe_customer_id: plano === "premium" ? `cus_${slug.slice(0, 6)}${between(1000, 9999)}` : null,
      data_renovacao:
        plano === "premium"
          ? new Date(NOW + between(1, 30) * DAY).toISOString()
          : null,
      created_at: daysAgo(createdDays),
      banned,
      nome,
    });
  }
  return users.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export const users: MockUser[] = buildUsers();

// ── PLANS / BILLING (derivado dos users premium) ────────────────────────────────
function buildPlans(): PlanRecord[] {
  const STATUSES: PlanRecord["status"][] = [
    "active", "active", "active", "active", "trialing", "past_due", "canceled",
  ];
  return users
    .filter((u) => u.plano === "premium" && u.role !== "admin")
    .map((u, i) => {
      const status = i === 0 ? "active" : pick(STATUSES);
      const mrr_cents =
        status === "active" || status === "past_due"
          ? u.tipo_cobranca === "annual"
            ? Math.round(19900 / 12) // R$199/ano amortizado
            : 1990 // R$19,90/mês
          : 0;
      return {
        id: `sub_${u.id.slice(4)}`,
        user_id: u.id,
        email: u.email,
        plano: u.plano,
        tipo_cobranca: u.tipo_cobranca,
        stripe_customer_id: u.stripe_customer_id,
        stripe_subscription_id: `sub_stripe_${between(100000, 999999)}`,
        status,
        mrr_cents,
        data_renovacao: u.data_renovacao,
        created_at: u.created_at,
      } satisfies PlanRecord;
    });
}

export const plans: PlanRecord[] = buildPlans();

// ── AI METRICS (dia × provider × kind) ──────────────────────────────────────────
function buildAiMetrics(): AiMetric[] {
  // provider → kinds que ele atende + custo por unidade
  const MATRIX: { provider: string; kind: AiMetric["kind"] }[] = [
    { provider: "groq", kind: "stt" }, // whisper large v3
    { provider: "groq", kind: "llm" }, // llama
    { provider: "gemini", kind: "llm" },
    { provider: "assemblyai", kind: "stt" },
    { provider: "voyage", kind: "embedding" },
  ];
  const rows: AiMetric[] = [];
  let n = 0;
  for (let d = 29; d >= 0; d--) {
    const date = ymd(d);
    // fim de semana tem menos volume
    const dow = new Date(NOW - d * DAY).getUTCDay();
    const weekendFactor = dow === 0 || dow === 6 ? 0.45 : 1;
    for (const { provider, kind } of MATRIX) {
      const requests = Math.round(between(40, 900) * weekendFactor);
      if (requests === 0) continue;
      const tokens_in = kind === "embedding" ? requests * between(80, 200) : requests * between(120, 600);
      const tokens_out = kind === "llm" ? requests * between(60, 300) : 0;
      const audio_seconds = kind === "stt" ? requests * between(6, 40) : 0;
      // custo aproximado por tipo
      let cost_usd = 0;
      if (kind === "stt")
        cost_usd = (audio_seconds / 3600) * (provider === "assemblyai" ? 0.37 : 0.04);
      else if (kind === "llm")
        cost_usd = (tokens_in / 1e6) * 0.07 + (tokens_out / 1e6) * (provider === "gemini" ? 0.3 : 0.08);
      else cost_usd = (tokens_in / 1e6) * 0.12; // embedding voyage
      const fallback_count = kind === "stt" && chance(0.4) ? between(1, Math.max(1, Math.round(requests * 0.05))) : 0;
      rows.push({
        id: `aim_${String(++n).padStart(4, "0")}`,
        date,
        user_id: null, // agregado por dia/provider/kind
        provider,
        kind,
        requests,
        tokens_in,
        tokens_out,
        audio_seconds,
        cost_usd: round(cost_usd, 4),
        fallback_count,
        avg_latency_ms: between(180, kind === "stt" ? 2400 : 1200),
      });
    }
  }
  return rows.sort((a, b) => b.date.localeCompare(a.date));
}

export const aiMetrics: AiMetric[] = buildAiMetrics();

// ── BRAINS (moderação) ──────────────────────────────────────────────────────────
function buildBrains(): Brain[] {
  const owners = users.filter((u) => u.role !== "admin");
  const rows: Brain[] = [];
  for (let i = 0; i < 18; i++) {
    const [nome, emoji] = BRAIN_NAMES[i % BRAIN_NAMES.length];
    const owner = pick(owners);
    rows.push({
      id: `brn_${String(i + 1).padStart(4, "0")}`,
      user_id: owner.id,
      nome: i >= BRAIN_NAMES.length ? `${nome} ${Math.floor(i / BRAIN_NAMES.length) + 1}` : nome,
      emoji,
      is_default: chance(0.25),
      contexto: chance(0.6)
        ? `Contexto compartilhado sobre ${nome.toLowerCase()} entre os membros.`
        : null,
      members_count: between(1, 8),
      entries_count: between(0, 320),
      flagged: chance(0.12),
      created_at: daysAgo(between(5, 380)),
    });
  }
  return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export const brains: Brain[] = buildBrains();

// ── ENTRIES (~40, moderação) ────────────────────────────────────────────────────
function buildEntries(): Entry[] {
  const rows: Entry[] = [];
  for (let i = 0; i < 42; i++) {
    const categoria = pick(CATEGORIES);
    const modo = chance(0.18) ? pick(MODOS) : "normal";
    const brain = pick(brains);
    const is_private = modo === "desabafo" || (categoria === "saude" && chance(0.4));
    const texto_original = pick(ENTRY_TEXT[categoria]);
    const hasReminder = categoria === "lembrete" || categoria === "evento" || categoria === "saude";
    rows.push({
      id: `ent_${String(i + 1).padStart(4, "0")}`,
      user_id: brain.user_id,
      brain_id: brain.id,
      categoria,
      texto_original,
      texto_corrigido: chance(0.5) ? texto_original.replace(/\bpra\b/g, "para") : null,
      resumo: chance(0.6) ? texto_original.slice(0, 40) + "…" : null,
      dados_extraidos: hasReminder
        ? { quando: pick(["amanhã 8h", "quinta 14h", "dia 10", "sábado"]), local: chance(0.3) ? "escritório" : null }
        : {},
      data_evento: hasReminder ? new Date(NOW + between(1, 20) * DAY).toISOString() : null,
      data_hora_disparo: categoria === "lembrete" ? new Date(NOW + between(0, 10) * DAY).toISOString() : null,
      recorrencia: categoria === "saude" && chance(0.5) ? pick(["diaria", "semanal", "mensal"] as const) : null,
      modo,
      is_private,
      flagged: chance(0.14),
      created_at: daysAgo(between(0, 60)),
    });
  }
  return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export const entries: Entry[] = buildEntries();

// ── CORRECTIONS (matriz de→para) ────────────────────────────────────────────────
function buildCorrections(): Correction[] {
  const CAMPOS: Correction["campo_alterado"][] = [
    "categoria", "categoria", "categoria", "data_hora_disparo", "texto",
  ];
  // confusões mais comuns (de → para) que aparecem com mais frequência
  const CONFUSIONS: [Category, Category][] = [
    ["tarefa", "lembrete"], ["lembrete", "evento"], ["nota", "tarefa"],
    ["evento", "lembrete"], ["saude", "lembrete"], ["nota", "lembrete"],
  ];
  const rows: Correction[] = [];
  for (let i = 0; i < 34; i++) {
    const campo = pick(CAMPOS);
    const owner = pick(users.filter((u) => u.role !== "admin"));
    let categoria_de: Category | undefined;
    let categoria_para: Category | undefined;
    if (campo === "categoria") {
      // 70% das vezes uma das confusões comuns (para a matriz ficar realista)
      if (chance(0.7)) {
        const [de, para] = pick(CONFUSIONS);
        categoria_de = de;
        categoria_para = para;
      } else {
        categoria_de = pick(CATEGORIES);
        do {
          categoria_para = pick(CATEGORIES);
        } while (categoria_para === categoria_de);
      }
    }
    rows.push({
      id: `cor_${String(i + 1).padStart(4, "0")}`,
      user_id: owner.id,
      brain_id: chance(0.5) ? pick(brains).id : null,
      original: categoria_de ? { categoria: categoria_de } : { texto: "antes" },
      corrigido: categoria_para ? { categoria: categoria_para } : { texto: "depois" },
      campo_alterado: campo,
      categoria_de,
      categoria_para,
      created_at: daysAgo(between(0, 45)),
    });
  }
  return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export const corrections: Correction[] = buildCorrections();

// ── FEATURE FLAGS (8) ────────────────────────────────────────────────────────────
export const featureFlags: FeatureFlag[] = [
  { id: "ff_01", key: "stt_assemblyai_fallback", description: "Fallback de STT para AssemblyAI quando Groq falha.", enabled: true, rollout_percent: 100 },
  { id: "ff_02", key: "llm_scout_ab", description: "A/B entre Llama Scout e 3.3 70B na classificação.", enabled: true, rollout_percent: 50 },
  { id: "ff_03", key: "weekly_email_digest", description: "Resumo semanal por e-mail.", enabled: true, rollout_percent: 100 },
  { id: "ff_04", key: "brains_sharing", description: "Compartilhamento de Brains entre usuários.", enabled: false, rollout_percent: 10 },
  { id: "ff_05", key: "voice_entry", description: "Criar entrada por voz (transcrição).", enabled: true, rollout_percent: 80 },
  { id: "ff_06", key: "premium_annual_promo", description: "Banner de promoção do plano anual.", enabled: false, rollout_percent: 0 },
  { id: "ff_07", key: "smart_reminders", description: "Lembretes inteligentes com reagendamento.", enabled: true, rollout_percent: 35 },
  { id: "ff_08", key: "lgpd_export_data", description: "Exportação de dados pessoais (LGPD).", enabled: true, rollout_percent: 100 },
].map((f, i) => ({ ...f, updated_at: daysAgo(between(1, 60) + i) }));

// ── HEALTH ──────────────────────────────────────────────────────────────────────
export const health: HealthCheck[] = [
  { component: "postgres", status: "up", latency_ms: 12, detail: "Neon · pool 4/20 conexões ativas" },
  { component: "redis", status: "up", latency_ms: 3, detail: "Upstash · OK" },
  { component: "bullmq", status: "up", latency_ms: 8, detail: "fila classify: 2 waiting, 0 failed" },
  { component: "groq", status: "degraded", latency_ms: 2100, detail: "latência STT acima do normal" },
  { component: "gemini", status: "up", latency_ms: 640, detail: "OK" },
  { component: "voyage", status: "up", latency_ms: 410, detail: "embeddings OK" },
  { component: "assemblyai", status: "up", latency_ms: 980, detail: "fallback STT pronto" },
  { component: "stripe", status: "up", latency_ms: 220, detail: "webhooks em dia" },
  { component: "smtp", status: "down", latency_ms: null, detail: "provedor de e-mail indisponível" },
].map((h, i) => ({ id: `hc_${String(i + 1).padStart(2, "0")}`, ...h, checked_at: daysAgo(0, 1) } as HealthCheck));

// ── ADMIN AUDIT LOG (~40 eventos) ─────────────────────────────────────────────────
function buildAuditLog(): AdminAuditLog[] {
  // Admins que aparecem como autores das ações (o admin fixo + um colega).
  const ADMINS: { id: string; email: string }[] = [
    { id: "usr_admin_0001", email: "admin@melapp.com.br" },
    { id: "usr_admin_0002", email: "moderacao@melapp.com.br" },
  ];
  // Alvos: usuários comuns dos fixtures (ban/unban/impersonate) ou brains/entries (flag).
  const targetUsers = users.filter((u) => u.role !== "admin");

  // Motivos realistas por tipo de ação, alimentam `meta`.
  const BAN_REASONS = [
    "Spam de entradas automatizadas",
    "Conteúdo abusivo em brain compartilhado",
    "Violação dos termos de uso",
    "Fraude de pagamento detectada",
    "Solicitação de remoção (LGPD)",
  ];
  const FLAG_REASONS = [
    "Possível conteúdo sensível",
    "Denúncia de outro usuário",
    "Classificação automática de risco",
    "Revisão manual de qualidade",
  ];
  const IMPERSONATE_REASONS = [
    "Investigação de bug reportado no suporte",
    "Reprodução de erro de sincronização",
    "Atendimento ao usuário via ticket",
  ];

  const rows: AdminAuditLog[] = [];
  for (let i = 0; i < 40; i++) {
    const admin = pick(ADMINS);
    // distribuição: ban/flag mais comuns; impersonate/unban menos.
    const action: AuditAction = chance(0.32)
      ? "ban"
      : chance(0.5)
        ? "flag"
        : chance(0.5)
          ? "unban"
          : "impersonate";
    const target = pick(targetUsers);

    let meta: Record<string, unknown> = {};
    if (action === "ban") {
      meta = {
        reason: pick(BAN_REASONS),
        duration: pick(["permanente", "30 dias", "7 dias"]),
        ticket: chance(0.5) ? `SUP-${between(1000, 9999)}` : undefined,
      };
    } else if (action === "unban") {
      meta = {
        reason: pick(["Revisão aprovada", "Recurso aceito", "Engano na moderação"]),
        previous_ban: `SUP-${between(1000, 9999)}`,
      };
    } else if (action === "impersonate") {
      meta = {
        reason: pick(IMPERSONATE_REASONS),
        session_minutes: between(2, 25),
        ticket: `SUP-${between(1000, 9999)}`,
      };
    } else {
      // flag — pode mirar um brain/entry em vez do usuário
      const onBrain = chance(0.5);
      const target_ref = onBrain ? pick(brains).id : pick(entries).id;
      meta = {
        reason: pick(FLAG_REASONS),
        target_type: onBrain ? "brain" : "entry",
        target_ref,
        severity: pick(["baixa", "média", "alta"]),
      };
    }
    // remove chaves undefined para não poluir o meta.
    meta = Object.fromEntries(Object.entries(meta).filter(([, v]) => v !== undefined));

    rows.push({
      id: `aud_${String(i + 1).padStart(4, "0")}`,
      admin_id: admin.id,
      admin_email: admin.email,
      action,
      target_user_id: target.id,
      target_email: target.email,
      meta,
      created_at: daysAgo(between(0, 90)),
    });
  }
  return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export const auditLog: AdminAuditLog[] = buildAuditLog();

// ── Registro central: nome do resource → array de fixtures ─────────────────────────
// As entidades têm formato conhecido (sem index signature), por isso o cast
// explícito para o tipo de linha genérica que o mockDataProvider consome.
export const FIXTURES: Record<string, Array<Record<string, unknown>>> = {
  users,
  plans,
  "ai-metrics": aiMetrics,
  entries,
  brains,
  corrections,
  "feature-flags": featureFlags,
  health,
  "audit-log": auditLog,
} as unknown as Record<string, Array<Record<string, unknown>>>;
